import { InterviewProcessingState } from '@Domain/Enums/Interviews/InterviewProcessingState';
import { AlertTriangle, Hourglass, type LucideIcon, MicOff, Settings2, XCircle } from 'lucide-react';

export type Tone = 'accent' | 'warning' | 'danger' | 'muted';
export type Destination = 'upload' | 'workflow-choice';

export type Presentation = {
	readonly icon: LucideIcon;
	readonly tone: Tone;
	readonly message: string;
	readonly destination: Destination | null;
};

const WAITING_FOR_TRANSCRIPT = 'La transcription est en cours.';
const BLOCKED_TRANSCRIPT = 'La transcription est bloquée.';
const FAILED_TRANSCRIPT = 'La transcription a échoué.';

/** `null`: the interview succeeded, so its transcript is on its way and the review block takes the place. */
const transcriptPendingPresentations: Readonly<Record<InterviewProcessingState, Presentation | null>> =
	Object.freeze({
		[InterviewProcessingState.UPLOADING]: {
			icon: Hourglass,
			tone: 'accent',
			message: "L'enregistrement n'est pas encore arrivé.",
			destination: 'upload',
		},
		[InterviewProcessingState.AWAITING_CHOICE]: {
			icon: Hourglass,
			tone: 'accent',
			message: "Aucun traitement n'a été lancé pour cet enregistrement.",
			destination: 'workflow-choice',
		},
		[InterviewProcessingState.NO_WORKFLOW]: {
			icon: Settings2,
			tone: 'muted',
			message: "Aucune chaîne de traitement n'est configurée pour ce type d'enregistrement.",
			destination: null,
		},
		[InterviewProcessingState.NO_RECORDING]: {
			icon: MicOff,
			tone: 'muted',
			message: "L'enregistrement a été supprimé.",
			destination: null,
		},
		[InterviewProcessingState.RUNNING]: {
			icon: Hourglass,
			tone: 'accent',
			message: WAITING_FOR_TRANSCRIPT,
			destination: null,
		},
		[InterviewProcessingState.STALLED]: {
			icon: AlertTriangle,
			tone: 'warning',
			message: BLOCKED_TRANSCRIPT,
			destination: null,
		},
		[InterviewProcessingState.FAILED]: {
			icon: XCircle,
			tone: 'danger',
			message: FAILED_TRANSCRIPT,
			destination: null,
		},
		[InterviewProcessingState.PARTIAL]: {
			icon: XCircle,
			tone: 'danger',
			message: FAILED_TRANSCRIPT,
			destination: null,
		},
		[InterviewProcessingState.SUCCEEDED]: null,
		[InterviewProcessingState.HANDLED]: {
			icon: XCircle,
			tone: 'danger',
			message: FAILED_TRANSCRIPT,
			destination: null,
		},
	});

export default transcriptPendingPresentations;
